// Libs
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { User } from "firebase/auth";

// Local
import EmailTemplateForm from "../../components/EmailMarketing/EmailTemplateForm";
import {
  ExistingEmailTemplate,
  EmailTemplateStatus,
} from "../../models/email-template";
import { useAuthStore } from "../../store/firebase-auth";
import { useSiteKeyDocStore } from "../../store/site-key-doc";
import { EMAIL_TEMPLATES_URL } from "../../urls";
import { DbRead } from "../../database";
import { useToastMessageStore } from "../../store/toast-messages";

interface Props {
  siteKey: string;
}

interface FormData {
  name: string;
  subject: string;
  description?: string;
  htmlContent: string;
  status: EmailTemplateStatus;
}

export default function EmailTemplateDetailsContainer({ siteKey }: Props) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const firebaseUser = useAuthStore((state) => state.firebaseUser) as User;
  const addToastMessage = useToastMessageStore(
    (state) => state.addToastMessage,
  );
  const [template, setTemplate] = useState<ExistingEmailTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conversationID, setConversationID] = useState<string | undefined>();

  useEffect(() => {
    if (!id || !siteKey) {
      setLoading(false);
      return;
    }

    // Fetch the template by ID
    const fetchTemplate = async () => {
      try {
        const fetchedTemplate = await DbRead.emailTemplates.get(siteKey, id);
        setTemplate(fetchedTemplate);
        setError(null);
      } catch (error) {
        console.error("Error fetching template:", error);
        if (error instanceof Error) {
          setError(error.message);
        } else {
          setError("Failed to load template");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchTemplate();
  }, [id, siteKey]);

  const handleSubmit = async (data: FormData) => {
    if (!id || !firebaseUser) {
      addToastMessage({
        id: `auth-error-${Date.now()}`,
        message: "Missing template ID or user authentication",
        type: "error",
        dialog: false,
      });
      return;
    }

    setSaving(true);

    try {
      // Update the email template using our database function
      await DbRead.emailTemplates.update({
        siteKey,
        templateID: id,
        name: data.name,
        description: data.description,
        htmlContent: data.htmlContent,
        subject: data.subject,
        status: data.status,
      });

      // Show success message
      addToastMessage({
        id: `template-updated-${Date.now()}`,
        message: `Email template "${data.name}" updated successfully!`,
        type: "success",
        dialog: false,
      });

      // Stay on the current page instead of navigating away
      // User can manually navigate back if they want to
    } catch (error) {
      console.error("Error updating template:", error);

      // Show error message but keep user on the form
      addToastMessage({
        id: `template-update-error-${Date.now()}`,
        message:
          error instanceof Error
            ? `Failed to update template: ${error.message}`
            : "Failed to update template. Please try again.",
        type: "error",
        dialog: false,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateWithAI = async (data: {
    description: string;
    conversationID?: string;
  }) => {
    try {
      const result = await DbRead.stilton.generateEmailTemplate({
        siteKeyID: siteKey,
        description: data.description,
        conversationID: data.conversationID,
      });

      setConversationID(result.conversationID);
      return result;
    } catch (error) {
      console.error("Error generating template with AI:", error);
      throw error;
    }
  };

  const handleEditWithAI = async (data: {
    editInstructions: string;
    currentHtmlContent: string;
    currentSubject: string;
    conversationID?: string;
  }) => {
    try {
      const result = await DbRead.stilton.editEmailTemplate({
        siteKeyID: siteKey,
        editInstructions: data.editInstructions,
        currentHtmlContent: data.currentHtmlContent,
        currentSubject: data.currentSubject,
        conversationID: data.conversationID,
      });

      setConversationID(result.conversationID);
      return result;
    } catch (error) {
      console.error("Error editing template with AI:", error);
      throw error;
    }
  };

  const handleCancel = () => {
    navigate(EMAIL_TEMPLATES_URL);
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error && !template) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="max-w-md rounded-md border border-red-200 bg-red-50 p-4">
          <p className="text-red-800">Error loading template: {error}</p>
          <button
            onClick={() => navigate(EMAIL_TEMPLATES_URL)}
            className="mt-2 text-sm text-red-600 underline hover:text-red-800"
          >
            Back to Templates
          </button>
        </div>
      </div>
    );
  }

  if (!template && !loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="max-w-md rounded-md border border-yellow-200 bg-yellow-50 p-4">
          <p className="text-yellow-800">Template not found</p>
          <button
            onClick={() => navigate(EMAIL_TEMPLATES_URL)}
            className="mt-2 text-sm text-yellow-600 underline hover:text-yellow-800"
          >
            Back to Templates
          </button>
        </div>
      </div>
    );
  }

  return (
    <EmailTemplateForm
      initialData={
        template
          ? {
              name: template.name,
              subject: template.subject,
              description: template.description,
              htmlContent: template.htmlContent,
              status: template.status,
            }
          : undefined
      }
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      isEditing={true}
      isLoading={saving}
      templateId={template?.id}
      siteKey={siteKey}
      onGenerateWithAI={handleGenerateWithAI}
      onEditWithAI={handleEditWithAI}
      conversationID={conversationID}
    />
  );
}
